import {useEffect, useState} from "react";
import {Outlet, useLocation} from "react-router";
import {Box, Collapse, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, useTheme} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import {ChevronLeft, ExpandLess, ExpandMore, Menu} from "@mui/icons-material";
import {isUserAdmin} from "../utils";

export const NavigationDrawer = ({navOptions, currentNavOption, handleNavigate, filters, setFilters}) => {
    const theme = useTheme();
    const dims = theme.dimensions;
    const location = useLocation();
    const [navDrawerOpen, setNavDrawerOpen] = useState(false);
    const [subMenusOpen, setSubMenusOpen] = useState({audit: false, reports: false});

    useEffect(() => {
        const pathname = location.pathname;
        setSubMenusOpen({
            audit: pathname.startsWith('/audit'),
            reports: pathname.startsWith('/reports')
        });
    }, [location.pathname]);

    const handleNavDrawerOpen = () => {
        setNavDrawerOpen(true);
    };

    const handleNavDrawerClose = () => {
        setNavDrawerOpen(false);
        const updated = {...subMenusOpen};
        for (const subMenu of Object.keys(updated)) {
            updated[subMenu] = false;
        }
        setSubMenusOpen(updated);
    };

    const openedMixin = (theme) => ({
        width: dims.navDrawerWidth,
        height: `calc(100% - ${dims.headerHeight + dims.footerHeight}px)`,
        border: theme.palette.contentPanel.border,
        marginTop: dims.headerHeight,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
    });

    const closedMixin = (theme) => ({
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: `calc(${theme.spacing(7)} + 1px)`,
        height: `calc(100% - ${dims.headerHeight + dims.footerHeight}px)`,
        border: theme.palette.contentPanel.border,
        marginTop: dims.headerHeight,
        [theme.breakpoints.up('sm')]: {
            width: `calc(${theme.spacing(8)} + 1px)`,
        },
    });

    const DrawerHeader = styled('div')(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginTop: 8,
        marginBottom: 8
    }));

    const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'navDrawerOpen' })(({ theme }) => ({
            width: dims.navDrawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            variants: [
                {
                    props: ({ open }) => navDrawerOpen,
                    style: {
                        ...openedMixin(theme),
                        '& .MuiDrawer-paper': openedMixin(theme),
                    },
                },
                {
                    props: ({ open }) => !navDrawerOpen,
                    style: {
                        ...closedMixin(theme),
                        '& .MuiDrawer-paper': closedMixin(theme),
                    },
                },
            ],
        }),
    );

    const subMenu = (optionId, subMenuDef) => {
        return (
            <Collapse in={subMenusOpen[optionId]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {Object.entries(subMenuDef).map(([, subOptionDef]) => (
                        <ListItem key={subOptionDef.label} style={{padding: 0}} onClick={() => {
                            handleNavigate(subOptionDef);
                        }}>
                            <ListItemButton sx={{pl: 4}} selected={currentNavOption === subOptionDef}>
                                <ListItemIcon>
                                    {subOptionDef.icon}
                                </ListItemIcon>
                                <ListItemText primary={subOptionDef.label}/>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Collapse>
        );
    };

    const handleSubMenuExpand = (optionDefId) => {
        const updated = {...subMenusOpen};
        updated[optionDefId] = !subMenusOpen[optionDefId];
        setSubMenusOpen(updated);
    };

    return (
        <div style={{width: '100%'}}>
            <Box sx={{display: 'flex'}}>
                <Drawer variant="permanent" open={navDrawerOpen} anchor="top">
                    <DrawerHeader>
                        {!navDrawerOpen &&
                            <IconButton onClick={handleNavDrawerOpen} style={{marginRight: 12}}>
                                <Menu/>
                            </IconButton>
                        }
                        {navDrawerOpen &&
                            <IconButton onClick={handleNavDrawerClose} style={{marginRight: 12}}>
                                <ChevronLeft/>
                            </IconButton>
                        }
                    </DrawerHeader>
                    <Divider/>
                    <List>
                        {Object.entries(navOptions).map(([optionId, optionDef]) => (
                            ((!optionDef.adminOnly) || (optionDef.adminOnly && isUserAdmin())) &&
                            <div key={`${optionDef.label}-div`}>
                                {optionDef.adminOnly && <Divider style={{marginTop: 10, marginBottom: 10}}/>}
                                <ListItem key={optionDef.label} disablePadding sx={{display: 'block'}} onClick={() => {
                                    if(optionDef.subMenu) {
                                        handleNavDrawerOpen();
                                        handleSubMenuExpand(optionId, optionDef.subMenu);
                                    } else {
                                        handleNavigate(optionDef);
                                    }
                                }}>
                                    <ListItemButton sx={[{minHeight: 48, px: 2.5}, navDrawerOpen ? {justifyContent: 'initial'} : {justifyContent: 'center'}]}
                                                    selected={currentNavOption === optionDef}>
                                        <ListItemIcon sx={[{minWidth: 0, justifyContent: 'center'}, navDrawerOpen ? {mr: 3} : {mr: 'auto'}]}>
                                            {optionDef.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={optionDef.label} sx={[navDrawerOpen ? {opacity: 1} : {opacity: 0}]}/>
                                        {optionDef.subMenu && navDrawerOpen &&
                                            (subMenusOpen[optionId] ? <ExpandLess/> : <ExpandMore/>)
                                        }
                                    </ListItemButton>
                                </ListItem>
                                {optionDef.subMenu &&
                                    subMenu(optionId, optionDef.subMenu)
                                }
                            </div>
                        ))}
                    </List>
                </Drawer>
                <Box component="main" sx={{flexGrow: 1, p: 3, padding: 0, minWidth: 0}}>
                    <Outlet context={{filters, setFilters, navOptions, handleNavigate}}/>
                </Box>
            </Box>
        </div>
    );
};